/** @jsxImportSource theme-ui */
import React from 'react';

import { RiskDirectionType, RiskLikelihood } from 'models/RiskCategories';
import { valignHackBottom } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { InsightsIconLiteral } from 'components/_common/BdIcon/BdIcon';

import SvgRiskBars_HighDark from 'assets/RiskBars/risk-bars-high-dark.svg';
import SvgRiskBars_HighLight from 'assets/RiskBars/risk-bars-high-light.svg';
import SvgRiskBars_LowDark from 'assets/RiskBars/risk-bars-low-dark.svg';
import SvgRiskBars_LowLight from 'assets/RiskBars/risk-bars-low-light.svg';
import SvgRiskBars_ModerateDark from 'assets/RiskBars/risk-bars-moderate-dark.svg';
import SvgRiskBars_ModerateLight from 'assets/RiskBars/risk-bars-moderate-light.svg';
import SvgRiskBars_NegligibleDark from 'assets/RiskBars/risk-bars-negligible-dark.svg';
import SvgRiskBars_NegligibleLight from 'assets/RiskBars/risk-bars-negligible-light.svg';
import SvgRiskBars_VeryHigh from 'assets/RiskBars/risk-bars-very-high.svg';

type RiskLikelihoodIcon = {
  img: string;
  imgDark: string;
  bdIconName: { [key in RiskDirectionType]: InsightsIconLiteral };
};

const RiskLikelihoodIconMapping: Partial<{ [key in RiskLikelihood]: RiskLikelihoodIcon }> = {
  'Not calculated': {
    img: SvgRiskBars_NegligibleLight,
    imgDark: SvgRiskBars_NegligibleDark,
    bdIconName: {
      importation: 'icon-not-calculated-import',
      exportation: 'icon-not-calculated-export',
    },
  },
  'Calculating, revisit later!': {
    img: SvgRiskBars_NegligibleLight,
    imgDark: SvgRiskBars_NegligibleDark,
    bdIconName: {
      importation: 'icon-not-calculated-import',
      exportation: 'icon-not-calculated-export',
    },
  },
  Unlikely: {
    img: SvgRiskBars_NegligibleLight,
    imgDark: SvgRiskBars_NegligibleDark,
    bdIconName: {
      importation: 'icon-plane-import',
      exportation: 'icon-plane-export',
    },
  },
  Low: {
    img: SvgRiskBars_LowLight,
    imgDark: SvgRiskBars_LowDark,
    bdIconName: {
      importation: 'icon-plane-import',
      exportation: 'icon-plane-export',
    },
  },
  Moderate: {
    img: SvgRiskBars_ModerateLight,
    imgDark: SvgRiskBars_ModerateDark,
    bdIconName: {
      importation: 'icon-plane-import',
      exportation: 'icon-plane-export',
    },
  },
  High: {
    img: SvgRiskBars_HighLight,
    imgDark: SvgRiskBars_HighDark,
    bdIconName: {
      importation: 'icon-plane-import',
      exportation: 'icon-plane-export',
    },
  },
  'Very high': {
    img: SvgRiskBars_VeryHigh,
    imgDark: SvgRiskBars_VeryHigh,
    bdIconName: {
      importation: 'icon-plane-import',
      exportation: 'icon-plane-export',
    },
  },
};

interface RiskLikelihoodIconProps {
  riskLikelihood: RiskLikelihood;
  riskDirectionType: RiskDirectionType;
  dark?: boolean;
}

const RiskLikelihoodIcon: React.FC<RiskLikelihoodIconProps> = ({
  riskLikelihood,
  riskDirectionType,
  dark = false,
}) => {
  const riskLikelihoodIcon = RiskLikelihoodIconMapping[riskLikelihood];
  return (
    <span sx={{ whiteSpace: 'nowrap' }}>
      <img
        src={dark ? riskLikelihoodIcon.imgDark : riskLikelihoodIcon.img}
        height="16"
        alt=""
        sx={{
          verticalAlign: 'baseline !important',
          minWidth: '12px',
        }}
      />
      <BdIcon
        data-testid="riskLikelihoodIcon"
        color={dark ? 'stone10' : 'deepSea50'}
        name={riskLikelihoodIcon.bdIconName[riskDirectionType]}
        sx={{
          '&.icon.bd-icon': {
            mx: '2px',
            fontSize: '18px',
            '&.icon-plane-export': {
              ...valignHackBottom('-1px'),
            },
          },
        }}
      />
    </span>
  );
};

export default RiskLikelihoodIcon;
